// src/components/Header.js
import {
  Box,
  Heading,
  Image,
  Flex,
  HStack,
  Link as ChakraLink,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  VStack,
  Divider,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  HamburgerIcon,
  ChevronRightIcon,
  PhoneIcon,
  ChevronRightIcon as ArrowIcon,
} from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import headerImg from "../images/header.png";

// Nav — Admin Login intentionally lives only in the mobile drawer,
// keeping the main bar focused on what devotees need.
const navItems = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Deeds", path: "/deeds" },
  { label: "Expenditures", path: "/expenditures" },
  { label: "Contact", path: "/contact" },
];

const drawerItems = [
  ...navItems,
  { label: "Admin Login", path: "/admin/login" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isActive = (path) => location.pathname === path;

  return (
    <Box position="sticky" top={0} zIndex={100}>
      {/* ── Thin announcement strip — now with a clickable Donate link ── */}
      <Box bg="#854F0B" py={1.5} px={4}>
        <Flex
          maxW="1200px"
          mx="auto"
          align="center"
          justify="center"
          gap={3}
          wrap="wrap"
        >
          <Text fontSize="xs" color="white" fontWeight="500" textAlign="center">
            🙏 Construction underway — every contribution helps
          </Text>
          <ChakraLink
            onClick={() => navigate("/donate")}
            fontSize="xs"
            color="white"
            fontWeight="700"
            display="inline-flex"
            alignItems="center"
            gap={0.5}
            textDecoration="underline"
            _hover={{ color: "orange.100" }}
          >
            Donate Now <ArrowIcon boxSize={3} />
          </ChakraLink>
          <ChakraLink
            href="tel:+919493575676"
            fontSize="xs"
            color="white"
            fontWeight="700"
            display={{ base: "none", sm: "inline-flex" }}
            alignItems="center"
            gap={1}
            _hover={{ textDecoration: "underline" }}
          >
            <PhoneIcon boxSize={2.5} /> +91 9493575676
          </ChakraLink>
        </Flex>
      </Box>

      {/* ── Main nav — solid saffron, matches your brand ── */}
      <Box bg="saffron.500" boxShadow="0 2px 10px rgba(0,0,0,0.12)">
        <Flex
          maxW="1200px"
          mx="auto"
          align="center"
          justify="space-between"
          px={4}
          py={3}
          gap={3}
        >
          {/* Logo + Title */}
          <Flex align="center" gap={3} minW={0} flex="1">
            <Image
              src={headerImg}
              alt="Temple Logo"
              boxSize={{ base: "44px", md: "56px" }}
              objectFit="cover"
              borderRadius="full"
              border="2px solid"
              borderColor="whiteAlpha.600"
              flexShrink={0}
            />
            <Box minW={0}>
              <Heading
                as="h1"
                color="white"
                fontSize={{ base: "13px", md: "lg" }}
                lineHeight="1.35"
                noOfLines={2}
                fontWeight="700"
              >
                శ్రీ అన్నపూర్ణ సమేత విశ్వేశ్వర స్వామి ఆలయం తోకాడ
              </Heading>
              <Text
                fontSize={{ base: "8px", md: "10px" }}
                color="whiteAlpha.800"
                letterSpacing="0.06em"
                fontWeight="600"
                textTransform="uppercase"
                noOfLines={1}
              >
                Sri Annapurna Sametha Visweswara Swamy Alayam, Thokada
              </Text>
            </Box>
          </Flex>

          {/* Desktop nav — simple uppercase links, active page underlined in white */}
          <HStack
            spacing={7}
            display={{ base: "none", md: "flex" }}
            flexShrink={0}
          >
            {navItems.map((item) => (
              <ChakraLink
                key={item.label}
                onClick={() => navigate(item.path)}
                position="relative"
                fontSize="13px"
                fontWeight="700"
                letterSpacing="0.05em"
                textTransform="uppercase"
                color={isActive(item.path) ? "white" : "whiteAlpha.850"}
                _hover={{ textDecoration: "none", color: "white" }}
                pb="2px"
                borderBottom="2px solid"
                borderColor={isActive(item.path) ? "white" : "transparent"}
                transition="all 0.15s ease"
              >
                {item.label}
              </ChakraLink>
            ))}
          </HStack>

          {/* Mobile hamburger */}
          <IconButton
            aria-label="Menu"
            icon={<HamburgerIcon />}
            display={{ base: "flex", md: "none" }}
            onClick={onOpen}
            color="white"
            bg="whiteAlpha.200"
            _hover={{ bg: "whiteAlpha.300" }}
            borderRadius="10px"
            flexShrink={0}
          />
        </Flex>
      </Box>

      {/* Mobile Drawer */}
      <Drawer placement="right" onClose={onClose} isOpen={isOpen} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader
            fontWeight="700"
            color="#854F0B"
            borderBottomWidth="1px"
            borderColor="orange.100"
            fontSize="lg"
          >
            Menu
          </DrawerHeader>
          <DrawerBody px={3} py={4}>
            <VStack align="stretch" spacing={1}>
              {drawerItems.map((item) => (
                <Box key={item.label}>
                  {item.label === "Admin Login" && (
                    <Divider my={2} borderColor="orange.100" />
                  )}
                  <ChakraLink
                    onClick={() => {
                      navigate(item.path);
                      onClose();
                    }}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={3}
                    py={3}
                    borderRadius="10px"
                    fontWeight="700"
                    fontSize="13px"
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                    color={isActive(item.path) ? "#854F0B" : "gray.700"}
                    bg={isActive(item.path) ? "orange.50" : "transparent"}
                    _hover={{
                      textDecoration: "none",
                      bg: "orange.50",
                      color: "#854F0B",
                    }}
                    transition="all 0.15s ease"
                  >
                    {item.label}
                    <ChevronRightIcon color="orange.300" />
                  </ChakraLink>
                </Box>
              ))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
